import React, { useEffect, useState } from 'react';
import { GEMINI_API_KEY_LINK } from '../utils/providers/gemini/client';
import { X } from 'lucide-react';
import { useStore } from '../store/useStore';
import { translations } from '../utils/translations';

type TranslationKey =
  | 'apiConfiguration'
  | 'provider'
  | 'selectProvider'
  | 'apiKey'
  | 'getApiKey'
  | 'endpointUrl'
  | 'configuring'
  | 'saveConfiguration'
  | 'configured';

type TranslationType = {
  [key in TranslationKey]: string;
};

type Translations = {
  [key: string]: TranslationType;
};

// Fallback translations in case the import fails
const fallbackTranslations: Translations = {
  en: {
    apiConfiguration: 'API Configuration',
    provider: 'Provider',
    selectProvider: 'Select a provider',
    apiKey: 'API Key',
    getApiKey: 'Get API Key',
    endpointUrl: 'Endpoint URL',
    configuring: 'Configuring...',
    saveConfiguration: 'Save Configuration',
    configured: 'Configured'
  },
  es: {
    apiConfiguration: 'Configuración de API',
    provider: 'Proveedor',
    selectProvider: 'Seleccionar proveedor',
    apiKey: 'Clave API',
    getApiKey: 'Obtener clave API',
    endpointUrl: 'URL del endpoint',
    configuring: 'Configurando...',
    saveConfiguration: 'Guardar configuración',
    configured: 'Configurado'
  }
};

const PROVIDERS = [
  {
    id: 'anthropic',
    name: 'Anthropic',
    docsUrl: 'https://console.anthropic.com/',
    requiresTopup: true,
    icon: '/provider-icons/anthropic-logo.png'
  },
  {
    id: 'cerebras',
    name: 'Cerebras',
    docsUrl: 'https://cloud.cerebras.ai/platform/org_ed2xyytct4k4y5d5wyetrfev/apikeys',
    requiresTopup: false,
    icon: '/provider-icons/cerebras-logo.png'
  },
  {
    id: 'deepseek',
    name: 'Deepseek',
    docsUrl: 'https://platform.deepseek.com/api_keys',
    requiresTopup: true,
    icon: '/provider-icons/deepseek-logo.webp'
  },
  {
    id: 'gemini',
    name: 'Gemini',
    docsUrl: GEMINI_API_KEY_LINK,
    requiresTopup: false,
    icon: '/provider-icons/gemini-new-dark-mode.webp'
  },
  {
    id: 'groq',
    name: 'Groq',
    docsUrl: 'https://console.groq.com/keys',
    requiresTopup: false,
    icon: '/provider-icons/groq-logo-transparent.webp'
  },
  {
    id: 'minimax',
    name: 'MiniMax',
    docsUrl: 'https://api.minimaxi.com/document/guides/faq#get-api-key',
    requiresTopup: true,
    icon: '/provider-icons/minimax-logo.jpg'
  },
  {
    id: 'mistral',
    name: 'Mistral',
    docsUrl: 'https://console.mistral.ai/api-keys/',
    requiresTopup: true,
    icon: '/provider-icons/mistral-logo.png'
  },
  {
    id: 'ollama',
    name: 'Ollama',
    docsUrl: 'https://ollama.ai/download',
    requiresTopup: false,
    icon: '/provider-icons/ollama-logo.png'
  },
  {
    id: 'openai',
    name: 'OpenAI',
    docsUrl: 'https://platform.openai.com/api-keys',
    requiresTopup: true,
    icon: '/provider-icons/openai-logo.png'
  },
  {
    id: 'xai',
    name: 'xAI (Grok)',
    docsUrl: 'https://console.x.ai/',
    requiresTopup: true,
    icon: '/provider-icons/xai-logo.webp'
  },
  {
    id: 'qwen',
    name: 'Qwen',
    docsUrl: 'https://dashscope-intl.aliyuncs.com/',
    requiresTopup: true,
    icon: '/provider-icons/qwen-logo.png'
  }
].sort((a, b) => a.name.localeCompare(b.name));

export const ConfigPanel = () => {
  const { 
    isConfigPanelOpen, 
    toggleConfigPanel, 
    addApiConfig, 
    apiConfigs,
    isLoading,
    error,
    language
  } = useStore();
  
  const [provider, setProvider] = React.useState('');
  const [apiKey, setApiKey] = React.useState('');
  const [endpoint, setEndpoint] = React.useState('');
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [_, forceUpdate] = React.useState(0);

  // Get translation with fallback
  const getTranslation = (key: TranslationKey) => {
    try {
      return translations[language]?.[key] || fallbackTranslations[language]?.[key] || fallbackTranslations['en'][key];
    } catch (e) {
      console.error('Translation error:', e);
      return fallbackTranslations['en'][key];
    }
  };

  // Force re-render when language changes
  useEffect(() => {
    console.log('Language changed in ConfigPanel:', language);
    console.log('Available translations:', translations);
    console.log('Current translations:', translations[language]);
    forceUpdate(prev => prev + 1);
  }, [language]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.provider-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown when config panel closes
  useEffect(() => {
    if (!isConfigPanelOpen) {
      setIsDropdownOpen(false);
    }
  }, [isConfigPanelOpen]);

  // Debug translations on every render
  console.log('Render debug - language:', language);
  console.log('Render debug - translations:', translations[language]);

  const configuredProviders = apiConfigs.map(config => config.provider);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const selectedProvider = PROVIDERS.find(p => p.id === provider);
    if (!selectedProvider) return;

    console.log('Submitting config:', { provider, apiKey });
    await addApiConfig({ 
      provider, 
      apiKey, 
      endpoint
    });
    
    if (!error) {
      setProvider('');
      setApiKey('');
      setEndpoint('');
      // Close the panel after successful configuration
      setTimeout(() => toggleConfigPanel(), 500);
    }
  };

  if (!isConfigPanelOpen) return null;

  const selectedProvider = PROVIDERS.find(p => p.id === provider);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">{getTranslation('apiConfiguration')}</h2>
          <button
            onClick={toggleConfigPanel}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Configured API Keys */}
          {apiConfigs.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {language === 'es' ? 'Claves API Configuradas' : 'Configured API Keys'}
              </h3>
              <div className="space-y-2">
                {apiConfigs.map((config) => {
                  const providerInfo = PROVIDERS.find(p => p.id === config.provider);
                  return (
                    <div key={config.provider} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        {providerInfo && (
                          <img
                            src={providerInfo.icon}
                            alt={providerInfo.name}
                            className="w-4 h-4 object-contain"
                          />
                        )}
                        <span className="text-sm font-medium">{providerInfo?.name || config.provider}</span>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (window.confirm(language === 'es'
                            ? `¿Estás seguro de que quieres eliminar la clave API de ${providerInfo?.name}?`
                            : `Are you sure you want to delete the ${providerInfo?.name} API key?`)) {
                            try {
                              await useStore.getState().removeApiConfig(config.provider);
                            } catch (error) {
                              console.error('Error removing API config:', error);
                            }
                          }
                        }}
                        className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('provider')}
            </label>
            <div className="relative provider-dropdown">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white text-left flex items-center justify-between"
                disabled={isLoading}
              >
                {provider ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={PROVIDERS.find(p => p.id === provider)?.icon}
                      alt={PROVIDERS.find(p => p.id === provider)?.name}
                      className="w-4 h-4 object-contain"
                    />
                    <span>{PROVIDERS.find(p => p.id === provider)?.name}</span>
                  </div>
                ) : (
                  getTranslation('selectProvider')
                )}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                  {PROVIDERS.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setProvider(p.id);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-100 ${
                        configuredProviders.includes(p.id) ? 'text-green-600 font-medium' : ''
                      }`}
                    >
                      <img
                        src={p.icon}
                        alt={p.name}
                        className="w-4 h-4 object-contain"
                      />
                      <span>
                        {p.name} {configuredProviders.includes(p.id) ? `(${getTranslation('configured')})` : ''} {p.requiresTopup ? '💳' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedProvider && (
              <div className="flex items-center mt-2 space-x-2">
                <img 
                  src={selectedProvider.icon} 
                  alt={selectedProvider.name} 
                  className="w-6 h-6 object-contain"
                />
                <a
                  href={selectedProvider.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {getTranslation('getApiKey')}
                </a>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getTranslation('apiKey')}
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>

          {provider === 'ollama' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {getTranslation('endpointUrl')}
              </label>
              <input
                type="url"
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                placeholder="http://localhost:11434"
                required
                disabled={isLoading}
              />
            </div>
          )}

          <button
            type="submit"
            className={`w-full py-2 px-4 bg-gray-700 text-white rounded-md transition-colors ${
              isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
            }`}
            disabled={isLoading || !provider}
          >
            {isLoading ? getTranslation('configuring') : getTranslation('saveConfiguration')}
          </button>
        </form>
      </div>
    </div>
  );
};